<template>
  <div v-if="user">
    <base-page-heading
      :title="user.name"
      show-back-button
    >
      <template #secondary-title>
        <sfs-active-badge :active="!user.deleted_at" />
      </template>
      <template #extra>
        <b-button
          v-if="$can('update', 'User')"
          variant="primary"
          v-b-modal.technician-user-edit-modal
          class="mr-2 d-none d-md-inline"
          v-b-tooltip.hover
          title="Edit the User"
        >
          <i class="fa fa-pencil-alt mr-md-1" />
          <span class="d-none d-md-inline">Edit</span>
        </b-button>

        <b-dropdown
          variant="outline-secondary"
          no-caret
          right
        >
          <template #button-content>
            <i class="fa fa-ellipsis-v" />
          </template>

          <div class="d-md-none">
            <b-dropdown-item
              v-if="$can('update', 'User')"
              v-b-modal.technician-user-edit-modal
            >
              <i class="fa fa-pencil-alt mr-1" /> Edit
            </b-dropdown-item>

            <b-dropdown-divider />
          </div>

          <b-dropdown-item
            v-if="$can('destroy', 'User')"
            @click="toggleActive"
          >
            <i
              v-if="!user.deleted_at"
              class="fa fa-trash mr-1"
            />
            <i
              v-else
              class="fa fa-undo mr-1"
            />
            {{ toggleActiveTooltipText }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </base-page-heading>

    <div class="content pt-lg-5">
      <b-row class="mb-3">
        <b-col md="6">
          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Email
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :href="`mailto:${user.email}`">
                    {{ user.email }}
                  </b-link>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Phone
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :href="`tel:${user.phone}`">
                    {{ user.phone }}
                  </b-link>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row v-if="hasAnyAddressFields">
            <b-col>
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Address
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <div v-if="user.address">
                    {{ user.address }}
                  </div>
                  <div>
                    {{
                      {
                        city: user.city,
                        state: user.state,
                        zip_code: user.zip_code,
                      } | locationFormat
                    }}
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Time Zone
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ userTimezoneFormat(user.time_zone) }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Language Preference
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ user.locale }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Work Order Access
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-badge :variant="user.parent_work_order_access ? 'success' : 'secondary'">
                    {{ user.parent_work_order_access ? 'Full Access' : 'Only Assigned' }}
                  </b-badge>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>

        <b-col md="6">
          <hr class="mt-0 mb-3 d-md-none">

          <b-row>
            <b-col cols="12">
              <b-tabs content-class="pt-3">
                <b-tab title="Mobile Devices">
                  <div v-if="sortedMobileDevices.length">
                    <div
                      v-for="device in visibleMobileDevices"
                      :key="device.deviceName + device.last_seen_at"
                      class="mb-2"
                    >
                      <b-row>
                        <b-col
                          cols="auto"
                          class="mr-auto font-w600"
                        >
                          {{ device.deviceName }}
                        </b-col>
                        <b-col class="text-right">
                          {{ device.os }} {{ device.os_version }}
                        </b-col>
                      </b-row>
                      <b-row
                        v-if="device.last_seen_at"
                        class="font-size-sm text-secondary"
                      >
                        <b-col>
                          Last seen {{ device.last_seen_at | dateTimeFormat }}
                        </b-col>
                      </b-row>
                    </div>

                    <div
                      v-if="sortedMobileDevices.length > 5"
                      class="mt-3"
                    >
                      <b-link @click="showAllDevices = !showAllDevices">
                        {{ showAllDevices ? 'Show Less' : 'Show More' }}
                      </b-link>
                    </div>
                  </div>
                  <div v-else>
                    <b-row>
                      <b-col
                        cols="auto"
                        class="mr-auto"
                      >
                        -
                      </b-col>
                    </b-row>
                  </div>
                </b-tab>
                <b-tab title="App Versions">
                  <div v-if="sortedMobileVersions.length">
                    <b-row
                      v-for="entry in visibleMobileVersions"
                      :key="entry.version + entry.lastSeen"
                      class="mb-2"
                    >
                      <b-col
                        cols="auto"
                        class="mr-auto font-w600"
                      >
                        {{ entry.version }}
                      </b-col>
                      <b-col class="text-right text-secondary">
                        {{ entry.lastSeen | dateTimeFormat }}
                      </b-col>
                    </b-row>

                    <div
                      v-if="sortedMobileVersions.length > 5"
                      class="mt-3"
                    >
                      <b-link @click="showAllAppVersions = !showAllAppVersions">
                        {{ showAllAppVersions ? 'Show Less' : 'Show More' }}
                      </b-link>
                    </div>
                  </div>
                  <div v-else>
                    <b-row>
                      <b-col
                        cols="auto"
                        class="mr-auto"
                      >
                        -
                      </b-col>
                    </b-row>
                  </div>
                </b-tab>
              </b-tabs>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>

    <!-- Modals -->
    <edit-modal
      v-if="$can('update', 'User')"
      v-model="user"
    />
  </div>
</template>

<script>
import formMixin from '@/mixins/formMixin';
import EditModal from '@/components/technician/users/EditModal.vue';
import { DateTime } from 'luxon';

export default {
  name: 'TechnicianUsersShow',
  mixins: [formMixin],
  components: {
    EditModal,
  },
  data() {
    return {
      user: null,
      timezoneOptions: [],
      showAllDevices: false,
      showAllAppVersions: false,
    };
  },
  computed: {
    hasAnyAddressFields() {
      return (
        (this.user.address || '').length > 0 ||
        (this.user.city || '').length > 0 ||
        (this.user.state || '').length > 0 ||
        (this.user.zip_code || '').length > 0
      );
    },
    toggleActiveTooltipText() {
      return !this.user.deleted_at ? 'Deactivate the User' : 'Reactivate the User';
    },
    toggleActiveConfirmText() {
      return !this.user.deleted_at
        ? 'Are you sure you want to cancel the User?'
        : 'Are you sure you want to reactivate the User?';
    },
    sortedMobileDevices() {
      if (!this.user || !this.user.devices) {
        return [];
      }

      return Object.entries(this.user.devices)
        .map(([deviceName, data]) => ({ deviceName, ...data }))
        .sort((a, b) => {
          return new Date(b.last_seen_at) - new Date(a.last_seen_at);
        });
    },
    visibleMobileDevices() {
      const devices = this.sortedMobileDevices;
      return this.showAllDevices ? devices : devices.slice(0, 5);
    },
    sortedMobileVersions() {
      if (!this.user || !this.user.app_versions) {
        return [];
      }

      return Object.entries(this.user.app_versions)
        .map(([version, lastSeen]) => ({ version, lastSeen }))
        .sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen));
    },
    visibleMobileVersions() {
      const versions = this.sortedMobileVersions;
      return this.showAllAppVersions ? versions : versions.slice(0, 5);
    },
  },
  mounted() {
    this.getUser();
    this.getTimeZoneOptions();
  },
  watch: {
    $route: 'getUser',
  },
  methods: {
    getUser() {
      this.$technicianUsersAPI
        .get(this.$route.params.id)
        .then((user) => (this.user = user))
        .catch(() => {
          this.$router.push('/404');
        });
    },
    getTimeZoneOptions() {
      this.$technicianUsersAPI.getTimeZoneOptions().then((response) => {
        this.timezoneOptions = response.data.time_zone_options.map((option) => {
          return { name: option.name, value: option.identifier };
        });
      });
    },
    userTimezoneFormat(timezone) {
      for (let i = 0; i < this.timezoneOptions.length; i++) {
        if(timezone === this.timezoneOptions[i].value) {
          timezone = this.timezoneOptions[i].name;
        }
      }
      return timezone;
    },
    toggleActive() {
      this.$swal({
        text: this.toggleActiveConfirmText,
        preConfirm: () => this.$technicianUsersAPI.update(this.$route.params.id, { deleted_at: !this.user.deleted_at ? DateTime.now().toISODate() : null }),
      }).then((result) => {
        if (result.value) {
          this.user = result.value;
        }
        result.timer;
      });
    },
  },
};
</script>
